import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { RaciTemplateModel } from '../models/raci-template-model';
import { RaciTemplateOverviewModel } from '../models/raci-template-model-overview';
import { AppServiceHelperService } from '../../../services/app-service-helper.service';
import { EventBusService } from '../../../services/event-bus.service';

/**
 * RACI template service
 */
@Injectable({
  providedIn: 'root'
})
export class RaciTemplateService {
  /**
   * Search active as observable
   */
  searchActive$: Observable<boolean>;

  /**
   * BehaviorSubject that holds the current search active state and initializes it with false
   */
  private searchActive = new BehaviorSubject<boolean>(false);

  /**
   * Observable that emits the current reset search state
   */
  resetSearch$: Observable<boolean>;

  /**
   * BehaviorSubject that holds the current reset search state and initializes it with false
   */
  private resetSearch = new BehaviorSubject<boolean>(false);

  /**
   * Observable that emits the current selected ID
   */
  selectedId$: Observable<number>;

  /**
   * BehaviorSubject that holds the current selected ID and initializes it with 0
   */
  private selectedId = new BehaviorSubject<number>(0);

  /**
   * The ID of the selected template
   */
  private selectedTemplateId: number;

  /**
  * Observable that emits the current disable edit state
  */
  disableEdit$: Observable<boolean>;

  /**
   * BehaviorSubject that holds the current disable edit state and initializes it with true
   */
  private disableEdit = new BehaviorSubject<boolean>(true);

  /**
   * The resource for the RaciTemplate
   */
  private _raciTemplateResource: string;

  /**
   * The resource for the Reflist
   */
  private _reflistResource: string;

  /**
   * The selected RaciTemplate
   */
  private selectedTemplate: RaciTemplateModel;

  /**
   * Constructor
   * @param httpClient
   * @param appServiceHelper
   * @param eventBus
   */
  constructor(private httpClient: HttpClient, private appServiceHelper: AppServiceHelperService, private eventBus: EventBusService) {
    this._raciTemplateResource = this.appServiceHelper.getServiceEndpoint('pollux.settings.api', 'racitemplate');
    this.searchActive$ = this.searchActive.asObservable();
    this.resetSearch$ = this.resetSearch.asObservable();
    this.selectedId$ = this.selectedId.asObservable();
    this.disableEdit$ = this.disableEdit.asObservable();
    this._reflistResource = this.appServiceHelper.getServiceEndpoint('pollux.api', 'reflist/pollux');
  }

  //#region API

  /**
   * Retrieves the list of RaciTemplateOverviewModels
   * @returns An Observable emitting an array of RaciTemplateOverviewModel
   */
  getTemplates(): Observable<RaciTemplateOverviewModel[]> {
    return this.httpClient.get<RaciTemplateOverviewModel[]>(`${this._raciTemplateResource}/list`);
  }

  /**
   * Retrieves a specific RaciTemplateModel by ID
   * @param id The ID of the RaciTemplate to retrieve
   * @returns An Observable emitting a RaciTemplateModel
   */
  get(id: number): Observable<RaciTemplateModel> {
    return this.httpClient.get<RaciTemplateModel>(`${this._raciTemplateResource}/get?id=${id}`);
  }

  /**
   * Saves a RaciTemplateModel
   * @param template The RaciTemplateModel to save
   * @returns An Observable emitting void
   */
  save(template: RaciTemplateModel): Observable<void> {
    return this.httpClient.post<void>(`${this._raciTemplateResource}/save`, template);
  }

  /**
   * Updates a RaciTemplateModel
   * @param template The RaciTemplateModel to update
   * @returns An Observable emitting void
   */
  update(template: RaciTemplateModel): Observable<void> {
    return this.httpClient.post<void>(`${this._raciTemplateResource}/update`, template);
  }

  /**
   * Checks if a step can be deleted for a given template ID
   * @param templateId The ID of the template to check
   * @returns An Observable emitting a boolean value indicating if the step can be deleted
   */
  getCanStepBeDeleted(templateId: number): Observable<boolean> {
    return this.httpClient.get<boolean>(`${this._raciTemplateResource}/stepDelete?templateId=${templateId}`);
  }

  /**
   * Copies a RaciTemplateModel
   * @param template The RaciTemplateModel to copy
   * @returns An Observable emitting void
   */
  copy(template: RaciTemplateModel): Observable<void> {
    return this.httpClient.post<void>(`${this._raciTemplateResource}/copy`, template);
  }

  /**
  *  Load reflist
  */
  loadReflist(name: string, locale: string = null) {
    let url = `${this._reflistResource}?reflistName=${name}&withParentNo=false`;
    url = locale ? url + `&localeOverride=${locale.replace('-', '_')}` : url;

    return this.httpClient.get(url);
  }
  /**
   * Retrieves the template status as a string based on the provided ID
   * @param id The ID of the template status
   * @returns The template status as a string
   */
  getTemplateStatusById(id: number): string {
    switch (id) {
      case 0:
        return "Default";
      case 1:
        return "Active";
      case 2:
        return "Inactive";
    }
  }

  /**
   * Sets the search active state
   * @param active The new value for the search active state
   */
  setSearchActive(active: boolean) {
    this.searchActive.next(active);
  }

  /**
   * Triggers a reset search action
   */
  triggerResetSearch() {
    this.resetSearch.next(true);
  }

  /**
   * Triggers the selection of a specific ID
   * @param id The ID to be selected
   */
  triggerSelectedId(id: number): void {
    this.selectedId.next(id);
  }

  /**
   * Sets the selected template ID
   * @param id The ID of the selected template
   */
  setSelectedTemplateId(id: number) {
    this.selectedTemplateId = id;
  }

  /**
   * Retrieves the selected template ID
   * @returns The selected template ID
   */
  getSelectedTemplateId() {
    return this.selectedTemplateId;
  }

  /**
   * Sets the selected template
   * @param selectedItem The selected template to set
   */
  setSelectedTemplate(selectedItem: RaciTemplateModel) {
    this.selectedTemplateId = selectedItem.oid;
    this.selectedTemplate = selectedItem;
  }

  /**
   * Retrieves the selected template
   * @returns The selected template
   */
  getSelectedTemplate() {
    return this.selectedTemplate;
  }

  /**
   * Sets the disable edit state
   * @param disableEdit The new value for the disable edit state
   */
  setDisableEdit(disableEdit: boolean) {
    this.disableEdit.next(disableEdit);
  }

  /**
   * Show or hide data loader
   * @param state State
   */
  displayDataLoader(state: boolean) {
    this.eventBus.broadcastEvent("dataLoader", {
      displayLoader: () => {
        return state;
      }
    });
  }
}
